/**
 * One place for category presentation across every template surface (gallery card,
 * detail pages, marketplace). Labels come from the server facets (`categoryLabelMap`):
 * the shared vocabulary (content/taxonomy.yaml) first, then each library's own list.
 *
 * Colours: a category in the shared vocabulary takes the palette colour at its position
 * in the vocabulary, so a topic added to content/taxonomy.yaml colours itself with no
 * edit here. The palette is ordered so today's shared keys keep their old colours
 * (research blue, code ink, security red); violet, which was Original's while Original
 * was the first category, moved to the end when Original became a keyword, so no topic
 * changed colour. A key only a library lists (start, pro, school, create) keeps its
 * fallback colour below until the content backfill retires it.
 */
export const CATEGORY_PALETTE: readonly string[] = [
  "var(--brand-blue)",
  "var(--brand-green)",
  "var(--brand-coral)",
  "var(--brand-x-5e4ea0)",
  "var(--brand-ink-deep)",
  "var(--brand-danger)",
  "var(--brand-x-3a6a9e)",
  "var(--brand-x-8a7330)",
  "var(--brand-x-1f8a5b)",
  "var(--brand-violet-deep)",
  "var(--brand-green-deep)",
  "var(--brand-violet)",
];

/** Fallback colours for the library-era keys, used when a key is not in the vocabulary. */
export const CATEGORY_COLOR: Record<string, string> = {
  start: "var(--brand-muted)",
  research: "var(--brand-blue)",
  school: "var(--brand-green)",
  create: "var(--brand-coral)",
  pro: "var(--brand-violet)",
  security: "var(--brand-danger)",
  code: "var(--brand-ink-deep)",
};

/**
 * The avatar colour for a category. `categoryOrder` is the vocabulary's category keys in
 * file order (`facets.vocabulary.categories`); without it, or for a key it does not hold,
 * the library-era fallback applies, then grey.
 */
export function templateColor(
  categoryKey: string | null | undefined,
  categoryOrder?: readonly string[],
): string {
  const key = categoryKey ?? "";
  const position = categoryOrder?.indexOf(key) ?? -1;
  if (position >= 0) return CATEGORY_PALETTE[position % CATEGORY_PALETTE.length] as string;
  return CATEGORY_COLOR[key] ?? "var(--brand-muted)";
}

/** Flatten the facets into a key→localized-label map: the shared vocabulary's categories
 * (content/taxonomy.yaml), so a key such as `documents` never shows raw once a template
 * carries it. A library's own category list is always empty (wire 5.15.0) and is not read. */
export function categoryLabelMap(
  facets: { vocabulary?: { categories?: { key: string; label: string }[] } } | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const category of facets?.vocabulary?.categories ?? []) {
    if (!map.has(category.key)) map.set(category.key, category.label);
  }
  return map;
}

/** Flatten the facets into a keyword key→localized-label map: the vocabulary's authored
 * keywords (a row's `tagKeys`) and derived capabilities (a row's `capabilityKeys`), so a
 * card or chip never shows a raw key while a label is known. A library's own tag list is
 * always empty (wire 5.15.0) and is not read. */
export function keywordLabelMap(
  facets:
    | {
        vocabulary?: {
          keywords?: { key: string; label: string }[];
          capabilities?: { key: string; label: string }[];
        };
      }
    | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const term of [
    ...(facets?.vocabulary?.keywords ?? []),
    ...(facets?.vocabulary?.capabilities ?? []),
  ]) {
    if (!map.has(term.key)) map.set(term.key, term.label);
  }
  return map;
}
